/**
 * SegmentHeading — Custom TipTap node extension.
 *
 * Represents a non-editable section heading that marks segment boundaries.
 * Using atom: true ensures the cursor cannot enter the heading node.
 *
 * Spec 08 §10.2
 */

import { Node, mergeAttributes } from '@tiptap/core'

export const SegmentHeading = Node.create({
  name: 'segmentHeading',
  group: 'block',
  atom: true,       // Invisible boundary marker used for segment mapping
  selectable: false, // Prevent selection of the heading node itself
  draggable: false,

  addAttributes() {
    return {
      sectionId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-section-id'),
        renderHTML: (attributes) => ({
          'data-section-id': attributes['sectionId'],
        }),
      },
      status: {
        default: 'fresh',
        parseHTML: (element) => element.getAttribute('data-segment-status') ?? 'fresh',
        renderHTML: (attributes) => ({
          'data-segment-status': attributes['status'],
        }),
      },
      sectionNumber: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-section-number'),
        renderHTML: (attributes) => {
          if (attributes['sectionNumber'] == null) return {}
          return { 'data-section-number': attributes['sectionNumber'] }
        },
      },
      headingText: {
        default: '',
        parseHTML: (element) => element.textContent ?? '',
        // Text is rendered as node content in renderHTML(), not as an HTML attribute.
        renderHTML: () => ({}),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'h2[data-section-id]' }]
  },

  renderHTML({ node, HTMLAttributes }) {
    // Keep boundary markers out of the visible editing surface.
    // The actual visible/editable headings come from segment content.
    const { headingText, ...attrs } = HTMLAttributes
    const resolvedHeadingText = (
      typeof node?.attrs?.['headingText'] === 'string'
        ? node.attrs['headingText']
        : (typeof headingText === 'string' ? headingText : '')
    )
    return [
      'h2',
      mergeAttributes(attrs, {
        'aria-hidden': 'true',
        style: 'display:none',
      }),
      resolvedHeadingText,
    ]
  },
})
